import { Component, effect, inject, computed } from '@angular/core';
import { BaseConfigurableComponent } from '@app/base-config.component';
import { ActivatedRoute } from '@angular/router';
import { timer } from 'rxjs';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, takeWhile, tap } from 'rxjs/operators';
import { BannerComponent } from '@app/components/shared/banner/banner.component';
import { SchedulerStepService } from '@app/services/step/scheduler-step.service';
import { CategoriesService } from '@app/services/categories/categories.service';
import { ModalService } from '@app/services/modal/modal.service';
import {CommonModule, ViewportScroller } from '@angular/common';
import { createEvent, download } from 'src/utils';
import { LoadingComponent } from '@app/components/global/loading/loading.component';
import { Appointment } from '@app/services/fetch/appointment';

@Component({
  selector: 'afcu-your-appointment',
  standalone: true,
  providers: [ModalService],
  imports: [BannerComponent, LoadingComponent, CommonModule],
  templateUrl: './your-appointment.component.html'
})
export class YourAppointmentComponent extends BaseConfigurableComponent {

  private router = inject(Router);  
  protected schedulerService = inject(SchedulerStepService);
  protected categoriesService = inject(CategoriesService);
  protected appointmentService = inject(Appointment);

  protected modalService = inject(ModalService);
  protected target: string = ''


  calendar = { date: '', time: '' }
  address = { street: '', city: '', stateZip: '' }
  category: ServiceList = {} as ServiceList

  apptID: string = ''

  bannerLongWait: { enable: boolean, banner: BannerType } = {
    enable: false,
    banner: {
      title: "",
      content: ""
    }
  }

  bannerSuccess: BannerType = {
    title: "",
    content:""
  }

  bannerCheckinLate: BannerType = {
    title: "",
    content: ""
  }
  
  bannerCheckinSuccess: BannerType = {
    title: "",
    content: ""
  }

  
  countdownSeconds = 10; // Default fallback value

  countdown = (() => {
    if (this.origin === 'queue' || this.origin === 'check-in') {
      const countdown$ = timer(0, 1000).pipe(
        map(i => this.countdownSeconds - i),      
        takeWhile(s => s >= 0, true),
        tap(s => {
          if (s === 0){
            this.appointmentService.request.set(null)
            this.router.navigate(['splash'], { queryParams: { origin: 'kiosk'}})
          } 
        })
      );
      return toSignal(countdown$, { initialValue: this.countdownSeconds });
    }
    return toSignal(timer(0, 1000).pipe(
      map(() => null), // Return null when countdown shouldn't run
      takeWhile(() => false) // Never complete
    ), { initialValue: null });
  })();

  constructor(override route: ActivatedRoute, private viewportScroller: ViewportScroller) {
    super('page')

    this.getOrigin()

    this.route.data.subscribe(data => {
      this.target = data['target']
    });

    this.apptID = this.route.snapshot.paramMap.get('id')!;
    effect(() => {
      if (!this.isLoading) {

        const settings = this.schedulerService.settings()
        if(settings){
          const branchSettings = (settings[this.schedulerService.branchSelected().branchID] !== undefined) ? settings[this.schedulerService.branchSelected().branchID] : settings['GLOBAL']

          if (this.origin === 'queue' || this.origin === 'check-in') {
            const completionTimeout = this.schedulerService.settings()['GLOBAL'].kiosk_completion_timeout

            if (completionTimeout && completionTimeout.enabled && completionTimeout.timeout) {
              // Convert milliseconds to seconds for the countdown
              this.countdownSeconds = Math.floor(completionTimeout.timeout / 1000);
            } else {
              this.countdownSeconds = 10; // Default fallback
            }
          }

          this.bannerLongWait = {
            enable: branchSettings.kiosk_walkin_long_wait.enabled,
            banner:  {
              title: branchSettings.kiosk_walkin_long_wait.text[this.textService.language().toLowerCase()].title,
              content:branchSettings.kiosk_walkin_long_wait.text[this.textService.language().toLowerCase()].description
            }
          }

          this.bannerSuccess = {
            title: branchSettings.kiosk_walkin_success_message.text[this.textService.language().toLowerCase()].title,
            content:branchSettings.kiosk_walkin_success_message.text[this.textService.language().toLowerCase()].description
          }

          this.bannerCheckinLate = {
            title:  branchSettings.kiosk_late_checkin_message.text[this.textService.language().toLowerCase()].title,
            content: branchSettings.kiosk_late_checkin_message.text[this.textService.language().toLowerCase()].description
          }
          
          this.bannerCheckinSuccess = {
            title: branchSettings.kiosk_checkin_success_message.text[this.textService.language().toLowerCase()].title,
            content:branchSettings.kiosk_checkin_success_message.text[this.textService.language().toLowerCase()].description
          }
          
          if (this.origin !== 'queue' && this.origin !== 'check-in') {
            const success = this.getText()?.['yourAppointment']?.['banner']?.['success'];
            this.bannerSuccess = {
              title: success?.['title'],
              content: success?.['content']
            };
          }
        }

        if (Object.keys(this.schedulerService.branchSelected()).length > 0) {
          const [street, city, stateZip] = this.schedulerService.branchSelected().address.split(',').map(part => part.trim())
          this.address = {
            street: street,
            city: city,
            stateZip: stateZip
          }
          if (this.schedulerService.payload()['calendar'] != undefined) {
            const [dateStr, timeStr] = this.schedulerService.payload()['calendar'].split('@').map((part: string) => part.trim());
            this.calendar = {
              date: dateStr,
              time: timeStr
            }
          }
        }



        if (Object.keys(this.categoriesService.services()).length > 0)
          this.category = this.categoriesService.services().serviceList.filter(category => category.service.name == this.schedulerService.payload()['services'])[0]
        else
          this.category = { service: this.schedulerService.payload()['category'] } as ServiceList
      }
    })

  }

  get isLoading() {
    return this.schedulerService.payload()['contact-info'] === undefined
  }

  toNumber(value: string): number {
    return Number(value)
  }

 createCalendar() {
  const payloadCal = this.schedulerService.payload()['calendar'].replace('@', '').trim();
  const startDate = new Date(payloadCal);
  const endDate = new Date(
    startDate.getTime() + this.category.service.duration * 60 * 1000
  );
  const timezone = this.schedulerService.branchSelected().timeZone
  download(
    'Addition-Appointment.ics',
    createEvent({
      appointment: {
        start: startDate,
        end: endDate
      },
      location: this.schedulerService.branchSelected().address,
      timezone: timezone,
      title: 'Appt: ' + this.category.service.name + ' at Addition Financial',
      description: this.icsDescription()
    })
  );
}

backToSplash() {
  this.appointmentService.request.set(null)
  this.appointmentService.unhandledError.set(false)
  this.router.navigate(['splash'], { queryParams: { origin: 'kiosk'}})
}

icsDescription(): string {
  const {
    fullName,
    email,
    phone: mobileNumber,
    appointment: receiveNotifications,
    membershipNumber
    } = this.schedulerService.payload()['contact-info'];
  const additionalDetails = this.category.service.serviceDetails;
  const appointmentTime = this.schedulerService.payload()['calendar'];
  const duration = this.category.service.duration;
  
  const lines = ['Appointment Details'];
  lines.push(`- Service: ${this.category.service.name}`)
  lines.push(`- Location: ${this.schedulerService.branchSelected().address}`)
  if (fullName) lines.push(`- Attendee: ${fullName}`);
  if (email) lines.push(`- Email: ${this.maskEmail(email)}`);
  if (mobileNumber) lines.push(`- Mobile number: ${this.maskPhone(mobileNumber)}`);
  if (receiveNotifications)
    lines.push(`- Receive notifications via text message: Yes`);
  else 
    lines.push(`- Receive notifications via text message: No`);
  if (membershipNumber) lines.push(`- Membership number: ${this.maskLast3(membershipNumber)}`);
  if (additionalDetails) lines.push(`- Additional details: ${additionalDetails}`);
  if (appointmentTime) lines.push(`- Appointment Time: ${appointmentTime}`);
  if (duration) lines.push(`- Expected service duration: ${duration} minutes`);
  return lines.join('\\n');
}



  openMapWithDirections(destination: string) {
    const googleMapUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    window.open(googleMapUrl, '_blank', 'noopener,noreferrer');
  }

  scroll() {
    this.viewportScroller.scrollToAnchor("service-detail")
  }

  maskEmail(email: string): string {
    const emailRegex = /^(.{3})[^@]*@([^@]+)\.([a-z]+)$/i;
    const match = email.match(emailRegex);

    if (!match) {
      return "Invalid email";
    }

    const [, userPrefix, domainName, domainExt] = match;
    const domainSuffix = domainName.slice(-3);
    return `${userPrefix}***@${'*'.repeat(domainName.length - 3)}${domainSuffix}.${domainExt}`;
  }

  maskLast3(value: string): string {
    if (value.length < 3) {
      return '*'.repeat(value.length); // Hide everything
    }

    const maskedPart = '*'.repeat(value.length - 3);
    const visiblePart = value.slice(-3);
    return maskedPart + visiblePart;
  }

  maskPhone(phone: string): string {
    const digits = phone.replace(/\D/g, ''); // Remove non-digit characters
    const visible = digits.slice(-4);
    return '***-***-' + visible;
  }
}
